import React, { useState, useEffect } from 'react';
import Card from '../Components/Modals/Card';
import { PaymentForm, CreditCard } from 'react-square-web-payments-sdk';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import ButtonPrimary from '../Components/subcomponents/ButtonPrimary';
import PhoneInput from '../Components/subcomponents/Phoneinput';
import currentDomain from '../Utils/Utils';
import { loadSquareSdk } from '../Utils/squareSdk';
import { detectInAppBrowser, androidExternalBrowserUrl } from '../Utils/inAppBrowser';
import reportClientError from '../Utils/reportClientError';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import builtWithSquareBadge from '../assets/square/built-with-square-black.png';

// How long to wait after square.js is loaded for the card fields to actually
// appear. <PaymentForm> renders nothing while the SDK initializes and swallows
// any error it hits doing so, so a failure there looks exactly like a form
// that never arrives. Generous, because this races a real network round trip.
const CARD_FIELDS_TIMEOUT_MS = 20_000;

// What to tell the buyer for each way the payment form can fail to appear.
// Every one of them ends the same way — pay by check, or call — because none
// of them are anything the buyer can fix by waiting.
// Last resort if /api/config/square can't be reached or comes back unusable.
// Both are public Square identifiers — they're published in the page's JS
// either way — so the only thing baking them in costs is that a checkout that
// loses the config endpoint charges production instead of failing. Anything
// that actually moves money still runs server-side against SQUARE_* env vars.
const SQUARE_CONFIG_FALLBACK = {
  applicationId: 'sq0idp-6qVvnz6DBT7FcV3lNxIA4A',
  locationId: 'LDAZZB6M2VPY7',
};

const SDK_ERROR_MESSAGES = {
  blocked:
    "Something on this device is blocking our payment provider. If you use an ad or content blocker, a VPN, or a school or work network, turn it off for this site and try again.",
  'no-global':
    "This network returned something unexpected instead of the payment form. If you're on public Wi-Fi that asks you to sign in, sign in first — or switch to cellular data — and try again.",
  timeout:
    "The payment form is taking too long to load. Your connection may be slow or unstable — check your signal and try again.",
  'no-card-fields':
    "The payment form loaded but the card fields didn't start up. Try again, or Call/text (913) 704-8131 and we'll sort it out.",
  'bad-app-id':
    "Card payments aren't configured correctly on our end. Call or text(913) 704-8131 and we'll sort it out.",
};

function Checkout(goBack, vehicles) {
  const [searchParams, setSearchParams] = useSearchParams();
  let success, vehicle_count, owner_id, owner_name, hasPaid, memo, vehicleList, entry_fee, total;
  let phone = searchParams.get('phone')
  let dev = searchParams.get('dev')
  const [checkoutState, setCheckoutState] = useState(0);
  // const [checkoutState, setCheckoutState] = useState(phone || phone == ''? 1 : 0);
  const [regUUID, setRegUUID] = useState('');
  const [memoTag, setMemoTag] = useState('');
  const [alreadyPaid, setAlreadyPaid] = useState(false);
  const [vehicleCount, setVehicleCount] = useState(1);
  // Both come from the server, which prices every charge from config.entryFee
  // and the registrant's unpaid cars. Shown here, never sent back.
  const [entryFee, setEntryFee] = useState(25.00);
  const [totalCost, setTotalCost] = useState(0);
  const [vehicleDescriptions, setVehicleDescriptions] = useState([]);
  // Square application/location IDs come from the server (/api/config/square),
  // so sandbox vs production is decided by the container's env vars at runtime.
  const [squareConfig, setSquareConfig] = useState(null);
  // 'loading' | 'ready' | 'error'. Covers fetching the Square keys, loading
  // square.js, and the SDK starting the card fields, because from the buyer's
  // side all three failures look identical: no card form, no explanation.
  const [sdkStatus, setSdkStatus] = useState('loading');
  const [sdkError, setSdkError] = useState(null);
  // Bumped by "Try again" to re-run the load from the top.
  const [loadAttempt, setLoadAttempt] = useState(0);
  // Most buyers arrive through the Facebook page, and its in-app webview
  // can't run Square's card iframe (see inAppBrowser.js). Detected once —
  // the user agent doesn't change mid-session.
  const [inAppBrowser] = useState(() => detectInAppBrowser());
  // Set by the "try it here anyway" link: skips the escape-hatch panel and
  // attempts the card form inside the webview after all. If that fails, the
  // normal no-card-fields error UI takes over.
  const [inAppAnyway, setInAppAnyway] = useState(false);
  const navigate = useNavigate();
  const showInAppEscape = Boolean(inAppBrowser) && !inAppAnyway;

  // Show the buyer a way out, and tell the server what happened — these
  // failures happen on devices we don't have and can't reproduce, so the
  // report is the only record that any of it occurred.
  const failPaymentForm = (reason, detail) => {
    setSdkError({ reason, detail });
    setSdkStatus('error');
    reportClientError({ area: 'square-sdk', reason, detail });
  };

  const retryPaymentForm = () => setLoadAttempt((attempt) => attempt + 1);

  // Fetch the Square keys and load square.js up front, so <PaymentForm> only
  // ever mounts with the SDK already in hand. Left to do it itself, it renders
  // an empty box on failure and never says why.
  useEffect(() => {
    if (checkoutState !== 2) return;
    if (showInAppEscape) {
      // No point loading Square into a webview it can't run in — the escape
      // panel is showing instead. Reported so the logs say how much checkout
      // traffic hits this wall (it's most of the Facebook clicks).
      reportClientError({
        area: 'square-sdk',
        reason: 'in-app-browser',
        detail: `${inAppBrowser.app} ${inAppBrowser.os}`,
      });
      return;
    }
    let cancelled = false;

    setSdkStatus('loading');
    setSdkError(null);

    (async () => {
      let config;
      try {
        const res = await fetch(`${currentDomain(location)}/api/config/square`);
        if (!res.ok) throw new Error(`/api/config/square responded ${res.status}`);
        config = await res.json();
        if (!config?.applicationId || !config?.locationId) {
          throw new Error('/api/config/square returned no applicationId/locationId');
        }
      } catch (e) {
        // Don't strand the buyer over a config lookup: fall back to the
        // production keys and keep going. Still reported, because a checkout
        // running on the fallback is a server problem someone has to fix.
        config = SQUARE_CONFIG_FALLBACK;
        reportClientError({ area: 'square-sdk', reason: 'config-fallback', detail: e?.message });
      }
      if (cancelled) return;
      setSquareConfig(config);

      try {
        await loadSquareSdk(config.applicationId);
      } catch (e) {
        if (!cancelled) failPaymentForm(e?.reason ?? 'load-failed', e?.message);
        return;
      }
      if (!cancelled) setSdkStatus('ready');
    })();

    return () => { cancelled = true; };
  }, [checkoutState, loadAttempt, showInAppEscape]);

  // square.js being loaded is not the same as the card fields being up: the
  // SDK can still fail bringing them online, and when it does it renders
  // nothing and throws the error away. If no card iframe has shown up by now,
  // treat it as a failure instead of leaving the buyer staring at a gap.
  //
  // Both places it can die are uncaught: the library calls Square.payments()
  // and card().attach() with no .catch() on either, so the real error escapes
  // as an unhandled promise rejection and the form just renders nothing.
  // Listening for that is the only way to find out what actually broke —
  // without it a report says "no iframe" and nothing more, which is exactly
  // what made the iPad failure impossible to diagnose.
  useEffect(() => {
    if (checkoutState !== 2 || sdkStatus !== 'ready') return;

    let sdkRejection = null;
    const onRejection = (event) => {
      const reason = event?.reason;
      sdkRejection = reason?.message ?? String(reason);
    };
    window.addEventListener('unhandledrejection', onRejection);

    const timer = setTimeout(() => {
      const container = document.querySelector('#rswps-card-container');
      if (container?.querySelector('iframe')) return;
      // Which half failed. No container at all means Square.payments() never
      // resolved and <PaymentForm> bailed to null; a container sitting there
      // empty means payments.card()/attach() is what died.
      const stage = container ? 'card-attach' : 'payments-init';
      failPaymentForm('no-card-fields', [
        `stage=${stage}`,
        `secureContext=${window.isSecureContext}`,
        `err=${sdkRejection ?? 'none captured'}`,
      ].join(' '));
    }, CARD_FIELDS_TIMEOUT_MS);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('unhandledrejection', onRejection);
    };
  }, [checkoutState, sdkStatus]);

  const setCheckoutInfo = async () => {
    ({ success, vehicle_count, owner_id, owner_name, hasPaid, memo, vehicleList, entry_fee, total } = await fetchCheckoutInfo(phone));
    if (success) {
      setRegUUID(owner_id);
      setMemoTag(memo);
      setAlreadyPaid(hasPaid);
      setVehicleCount(vehicle_count);
      setEntryFee(entry_fee);
      setTotalCost(total);
      setVehicleDescriptions(vehicleList || []);
      if (!dev) setCheckoutState(1);
      else setCheckoutState(Number(dev));
      if (hasPaid) {
        toast.error(`You have already paid for ${vehicle_count} vehicle(s). If you believe this is an error, please contact (913) 704-8131`, {autoClose: 10000});
        navigate('/')
      }
    } else {
      toast.error('No registration found for that phone number. Please try again or call/text Brady @ (913) 704-8131', {autoClose: 10000});
      setSearchParams({ phone: '' });
      setCheckoutState(0);
    }
  }

  useEffect(() => {
    if (phone) {
      setCheckoutInfo();
    } else {
      phone = ''
    }
  }, []);

  const handleClick = (num) => {
    setSearchParams({ phone: phone + String(num) });
  }


  const handleDelete = () => {
    if (phone && phone.length > 0) {
      setSearchParams({ phone: phone.slice(0, -1) });
    }
  }

  const fetchCheckoutInfo = async (phone) => {
    const result = await fetch(`${currentDomain(location)}/api/payments?phone=${phone}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      }
    })

    if (result.status !== 200) {
      return {
        success: false,
        vehicleCount: 0,
        owner_id: null,
        owner_name: null,
        hasPaid: false,
        memo: null,
        vehicleList: [],
        entry_fee: 0,
        total: 0
      }
    } else {
      const data = await result.json();
      console.log(data);
      return {
        success: data.success,
        vehicle_count: data.vehicle_count,
        owner_id: data.owner_id,
        owner_name: data.owner_name,
        hasPaid: data.hasPaid,
        memo: data.memo,
        vehicleList: data.vehicles,
        entry_fee: data.entry_fee,
        total: data.total
      };
    }

  }


// No amount is sent: the server prices the charge from config and the cars it
// is about to mark paid, so the browser cannot decide what a car costs.
const processPayment = async (regUUID, token, last4) => {
  const result = await fetch(`${currentDomain(location)}/api/payments`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      "regUUID": regUUID,
      "token": token,
      "last4": last4,
    }),
  });

  if (result.status !== 200) {
    toast.error("Payment failed. Please try again, or contact support at support@thebasehorcarshow.com");
    // Back to the card form. Staying on "Processing..." left the buyer on a
    // screen with no card fields and no way off it.
    setCheckoutState(2);
  }else {
    toast.success("Success");
    const receiptURL = await result.text()
    //redirect after 5 seconds
    setTimeout(() => {
      window.location.assign(receiptURL);
    }, 5000);
  }
}


  const renderPage = () => {
    return (
      <Card>
        {/* Phone Number Input */}
        {checkoutState == 0 && (
          <div>
            <div className='m-4'>
              <label className="pb-1 text-lg font-semibold"> Checkout by phone number </label>
              <br />
              <input
                type="text"
                value={phone || ''}
                onChange={(e) => setSearchParams({ phone: e.target.value })}
                placeholder="Enter phone number"
                className="mt-1 block w-full px-3 py-2 border border-gray"
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
                <button
                  key={num}
                  onClick={() => handleClick(num)}
                  className="bg-gray-100 hover:bg-gray-200 text-black font-bold py-4 px-4 rounded-md text-2xl"
                >
                  {num}
                </button>
              ))}
              <button
                onClick={handleDelete}
                className="bg-red-100 hover:bg-red-200 text-black font-bold py-4 px-4 rounded-md text-2xl"
              >
                ←
              </button>
              <button
                onClick={() => handleClick(0)}
                className="bg-gray-100 hover:bg-gray-200 text-black font-bold py-4 px-4 rounded-md text-2xl"
              >
                0
              </button>
              <ButtonPrimary type="button" onClick={() => setCheckoutInfo()} className="col-span-3">
                Continue
              </ButtonPrimary>
            </div>
          </div>

        )}


        {/* Registration Successful */}
        {checkoutState == 1 && (
          <div className="text-center py-8">
            <h1 className="font-bold text-4xl md:text-5xl">Registration Confirmed!</h1>
            <p className="mt-4 text-lg">Would you like to pay by check or by card?</p>
            <div className="mt-6 flex justify-center gap-4">
              <ButtonPrimary type="button" onClick={() => setCheckoutState(2)}>
                Pay by Card
              </ButtonPrimary>
              <ButtonPrimary type="button" onClick={() => setCheckoutState(3)}>
                Pay by Check
              </ButtonPrimary>
            </div>
          </div>
        )}


        {/* Payment Form for Card */}
        {checkoutState == 2 && (
          <div className="max-w-2xl mt-5 mx-auto px-4">
            {/* Cart Summary */}
            <div className="bg-gray-50 rounded-xl p-6 mb-8 border border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-1.5 6M7 13l-1.5-6M17 13v6a2 2 0 01-2 2H9a2 2 0 01-2-2v-6m8 0V9a2 2 0 00-2-2H9a2 2 0 00-2 2v4.01" />
                </svg>
                Order Summary
              </h3>
              
              <div className="space-y-4">
                {/* Order Item */}
                <div className="flex justify-between items-center py-3 border-b border-gray-200">
                  <div>
                    <h4 className="font-medium text-gray-900">Car Show Registration</h4>
                    <p className="text-sm text-gray-600">Entry fee for vehicle</p>
                  </div>
                  <span className="font-semibold text-gray-900">${entryFee.toFixed(2)} x {vehicleCount}</span>
                </div>

                {/* Vehicles being registered */}
                {vehicleDescriptions.length > 0 && (
                  <div className="py-2">
                    <p className="text-sm font-medium text-gray-700 mb-2">Your Vehicle{vehicleDescriptions.length > 1 ? 's' : ''}</p>
                    <ul className="space-y-2">
                      {vehicleDescriptions.map((v, i) => (
                        <li key={v.car_number ?? i} className="flex justify-between items-center bg-white rounded-lg border border-gray-200 px-3 py-2">
                          <span className="text-sm text-gray-900">
                            {[v.year, v.make, v.model].filter(Boolean).join(' ') || `Vehicle #${v.car_number}`}
                            {v.class_name && <span className="text-gray-500"> &middot; {v.class_name}</span>}
                          </span>
                          {v.ispaid && (
                            <span className="text-xs font-semibold text-green-700 bg-green-100 px-2 py-0.5 rounded-full">Paid</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Total */}
                <div className="flex justify-between items-center pt-4 border-t border-gray-300">
                  <span className="text-lg font-semibold text-gray-900">Total</span>
                  <span className="text-2xl font-bold text-green-600">${totalCost.toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Payment Form */}
            <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                  <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  Payment Information
                </h3>
                <img
                  src={builtWithSquareBadge}
                  alt="Built with Square"
                  className="h-10 w-auto"
                />
              </div>

              {showInAppEscape && (
                <div className="rounded-lg border border-blue-300 bg-blue-50 p-4">
                  <h4 className="font-semibold text-blue-900 mb-1">One quick step to pay by card</h4>
                  <p className="text-sm text-blue-900">
                    You&apos;re viewing this page inside the {inAppBrowser.app} app, and its built-in
                    browser can&apos;t display our secure card form. Opening this page in your
                    regular browser fixes it — your registration comes right along with you.
                  </p>
                  {inAppBrowser.os === 'android' ? (
                    <div className="mt-4">
                      <ButtonPrimary
                        type="button"
                        onClick={() => { window.location.href = androidExternalBrowserUrl(); }}
                      >
                        Open in my browser
                      </ButtonPrimary>
                    </div>
                  ) : (
                    <p className="mt-4 text-sm text-blue-900 font-medium">
                      Tap the <span className="font-bold">&middot;&middot;&middot;</span> menu in the
                      corner of your screen, then choose{' '}
                      <span className="font-bold">&ldquo;Open in browser&rdquo;</span>{' '}
                      (or &ldquo;Open in external browser&rdquo;).
                    </p>
                  )}
                  <div className="mt-4 flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={() => setCheckoutState(3)}
                      className="px-4 py-2 rounded-lg border border-blue-400 hover:bg-blue-100 text-blue-900 font-medium bg-white transition-colors"
                    >
                      Pay by check instead
                    </button>
                  </div>
                  <p className="mt-3 text-xs text-blue-800">
                    <button onClick={() => setInAppAnyway(true)} className="underline">
                      Try the card form here anyway
                    </button>
                    {' '}&middot; Stuck? Text or call Brady at{' '}
                    <a href="tel:+19137048131" className="font-semibold underline">+1 (913) 704-8131</a>.
                  </p>
                </div>
              )}

              {!showInAppEscape && sdkStatus === 'loading' && (
                <p className="text-gray-600 py-4">Loading payment form…</p>
              )}

              {sdkStatus === 'error' && (
                <div className="rounded-lg border border-amber-300 bg-amber-50 p-4">
                  <h4 className="font-semibold text-amber-900 mb-1">Card payment couldn&apos;t load</h4>
                  <p className="text-sm text-amber-900">
                    {SDK_ERROR_MESSAGES[sdkError?.reason] ?? "We couldn't start the card form on this device."}
                  </p>
                  <div className="mt-4 flex flex-col sm:flex-row gap-3">
                    <ButtonPrimary type="button" onClick={retryPaymentForm}>
                      Try again
                    </ButtonPrimary>
                    <button
                      onClick={() => setCheckoutState(3)}
                      className="px-4 py-2 rounded-lg border border-amber-400 hover:bg-amber-100 text-amber-900 font-medium bg-white transition-colors"
                    >
                      Pay by check instead
                    </button>
                  </div>
                  <p className="mt-3 text-xs text-amber-800">
                    Still stuck? Text or call Brady at{' '}
                    <a href="tel:+19137048131" className="font-semibold underline">+1 (913) 704-8131</a>.
                    {' '}(Ref: {sdkError?.reason ?? 'unknown'})
                  </p>
                </div>
              )}

              {sdkStatus === 'ready' && squareConfig && (
              <PaymentForm
                applicationId={squareConfig.applicationId}
                cardTokenizeResponseReceived={(token, verifiedBuyer) => {
                  // Only switch to "Processing..." once there is something to
                  // process. Doing it first stranded the buyer on that screen
                  // forever whenever tokenization came back with an error.
                  if (token.errors || !token.token) {
                    console.error('Tokenization errors:', token.errors);
                    toast.error('Payment failed. Please check your card details and try again.');
                    return;
                  }

                  setCheckoutState(4);
                  processPayment(regUUID, token.token, token.details.card.last4);
                }}
                locationId={squareConfig.locationId}
              >
                <CreditCard />
              </PaymentForm>
              )}

              <button
                onClick={() => setCheckoutState(1)}
                className="mt-4 w-full px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 text-gray-700 font-medium bg-white transition-colors"
              >
                ← Back
              </button>

              <div className="mt-6 pt-6 border-t border-gray-100 flex flex-col items-center gap-2">
                <p className="text-xs text-gray-500 text-center">
                  Your card details are encrypted and processed securely by Square. We never see or store your card number.
                </p>
              </div>
            </div>
            <div className="mt-8 text-right">
              <p className="text-gray-600">
                Something not look right? Text or call Brady at{' '}
                <a 
                  href="tel:+19137048131" 
                  className="text-blue-600 hover:text-blue-700 font-semibold hover:underline transition-colors"
                >
                  +1 (913) 704-8131
                </a>
                {' '}for assistance.
              </p>
            </div>
          </div>
        )}


        {/* Pay by Check*/}
        {checkoutState == 3 && (
        <div>
          <div className="pt-8 px-4 max-w-4xl mx-auto relative">
            <button
              onClick={() => setCheckoutState(1)}
              className="absolute top-4 right-4 px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 text-gray-700 font-medium bg-white shadow-sm transition-colors z-10"
            >
              ← Back
            </button>
            
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Pay by Check</h2>
              <p className="text-gray-600">Please follow the instructions below to complete your payment</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch">
              {/* Total Amount Due Section */}
              <div className="bg-green-50 rounded-xl p-6 text-center flex flex-col">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Total Amount Due</h3>
                <div className="bg-white rounded-lg p-4 shadow-sm border flex-grow flex flex-col justify-center">
                  <div className="text-3xl font-bold text-green-600 mb-2">${totalCost.toFixed(2)}</div>
                  <p className="text-sm text-gray-600">Registration Fee</p>
                  <p className="text-xs text-gray-600">$25 x {vehicleCount} Vehicle</p>
                </div>
              </div>

              {/* Payable To Section */}
              <div className="bg-gray-50 rounded-xl p-6 text-center flex flex-col">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Payable To</h3>
                <div className="bg-white rounded-lg p-4 shadow-sm border flex-grow flex flex-col justify-center">
                  <p className="font-bold text-lg text-gray-900 mb-2">Basehor VFW Car Show</p>
                  <address className="not-italic text-gray-700 text-sm leading-relaxed">
                    P.O. Box 280<br />
                    Basehor, Kansas 66007
                  </address>
                </div>
              </div>

              {/* Memo Line Section */}
              <div className="bg-blue-50 rounded-xl p-6 text-center flex flex-col">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Memo Line</h3>
                <div className="bg-white rounded-lg p-4 shadow-sm border flex-grow flex flex-col justify-center">
                  <div className="bg-blue-100 px-6 py-3 rounded-lg font-mono text-lg font-bold text-blue-900 inline-block mb-4">
                    {memoTag}
                  </div>
                  <p className="text-sm text-gray-600">Include this number on your check's memo line</p>
                </div>
              </div>
            </div>

            {/* Vehicles being registered */}
            {vehicleDescriptions.length > 0 && (
              <div className="mt-6 bg-gray-50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Your Vehicle{vehicleDescriptions.length > 1 ? 's' : ''}</h3>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {vehicleDescriptions.map((v, i) => (
                    <li key={v.car_number ?? i} className="flex justify-between items-center bg-white rounded-lg border border-gray-200 px-3 py-2">
                      <span className="text-sm text-gray-900">
                        {[v.year, v.make, v.model].filter(Boolean).join(' ') || `Vehicle #${v.car_number}`}
                        {v.class_name && <span className="text-gray-500"> &middot; {v.class_name}</span>}
                      </span>
                      {v.ispaid && (
                        <span className="text-xs font-semibold text-green-700 bg-green-100 px-2 py-0.5 rounded-full">Paid</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          {/* Questions Section at Bottom Right */}
            <div className="mt-8 text-right">
              <p className="text-gray-600">
                Questions? Text or call Brady at{' '}
                <a 
                  href="tel:+19137048131" 
                  className="text-blue-600 hover:text-blue-700 font-semibold hover:underline transition-colors"
                >
                  +1 (913) 704-8131
                </a>
                {' '}for assistance.
              </p>
            </div>
        </div>
        )}

        {checkoutState == 4 && (
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Processing...</h2>
          </div>
        )}
      </Card>
    )
  }

  return (<>{renderPage()}</>)
}

export default Checkout;